import { ReactElement, useEffect, useRef, useState } from "react"
import { AutoSizer, List, ListRowProps, WindowScroller } from "react-virtualized"
import { Button, Modal } from "@wfp/ui"
import { iconAddOutline } from "@wfp/icons"
import { faFingerprint } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Select, { SingleValue } from "react-select"
import useCommon from "hooks/useCommon"
import { useGetCategoriesFieldQuery } from "services/household/xlsFormSchemas"
import { filterOption, getGroupList } from "utils/helpers"
import { IChoiceForm, IGroupForm, IQuestionForm } from "interfaces/xlsForm"
import { FormField, OptionGroup } from "interfaces/mappingInterfaces"
import OverflowText from "components/Shared/OverflowText/OverflowText"
import CustomOption from "components/Shared/Common/CustomOption"
import QuestionForm from "components/Household/Form/QuestionForm"
import GroupForm from "components/Household/Form/GroupForm"

interface Props {
  xlsFormId: number
  permissions: boolean
  list: FormField[]
  saveQuestion: (payload: IQuestionForm, onClose: () => void) => Promise<void>
  saveGroup: (payload: IGroupForm, onClose: () => void) => Promise<void>
  loadingSaveQuestion: boolean
  loadingSaveGroup: boolean
  onUpdatePersonalInfo?: (field: FormField) => Promise<void>
}

const initialQuestionForm: IQuestionForm = {
  typeId: 0,
  name: "",
  label: "",
}

const initialGroupForm: IGroupForm = {
  name: "",
}

const initialChoiceForm: IChoiceForm = {
  name: "",
}

const FormList = ({
  xlsFormId,
  permissions,
  list,
  saveQuestion,
  saveGroup,
  onUpdatePersonalInfo,
  loadingSaveQuestion,
  loadingSaveGroup,
}: Props): ReactElement => {
  const listReference = useRef<List>(null)
  const { showSnackBar } = useCommon()

  const { data: categories } = useGetCategoriesFieldQuery()

  const [optionsGroup, setOptionsGroup] = useState<OptionGroup[]>([])
  const [currentGroupFilter, setCurrentGroupFilter] = useState<SingleValue<OptionGroup>>(null)
  const [filteredList, setFilteredList] = useState<FormField[]>([])
  const [openQuestionModal, setOpenQuestionModal] = useState<boolean>(false)
  const [openGroupModal, setOpenGroupModal] = useState<boolean>(false)
  const [questionForm, setQuestionForm] = useState<IQuestionForm>(initialQuestionForm)
  const [groupForm, setGroupForm] = useState<IGroupForm>(initialGroupForm)
  const [choiceForm, setChoiceForm] = useState<IChoiceForm>(initialChoiceForm)
  const [categoryType, setCategoryType] = useState<number>(0)

  const onClose = (): void => {
    openQuestionModal && setOpenQuestionModal(false)
    openGroupModal && setOpenGroupModal(false)
    categoryType && setCategoryType(0)
    setQuestionForm(initialQuestionForm)
    setGroupForm(initialGroupForm)
    setChoiceForm(initialChoiceForm)
  }

  const submitQuestion = async (): Promise<void> => {
    if (
      !questionForm.typeId ||
      !questionForm.name ||
      !questionForm.label ||
      (categoryType === categories?.List && !questionForm.choiceListId && !choiceForm.name)
    ) {
      showSnackBar(
        `Question type, name${categoryType === categories?.List ? ", choice list" : ""} and label are required`,
        "warning",
      )
      return
    }

    const payload = {
      xlsFormId,
      ...questionForm,
      ...(groupForm.name && { group: groupForm }),
      ...(choiceForm.name && { choiceList: choiceForm }),
    }
    await saveQuestion(payload, onClose)
  }

  const submitGroup = async (): Promise<void> => {
    if (!groupForm.name || !groupForm.label) {
      showSnackBar("Group name and label are required", "warning")
      return
    }

    await saveGroup(groupForm, onClose)
  }

  useEffect(() => {
    if (list?.length) {
      setFilteredList(
        list.filter(field => field.groupName.toLowerCase().includes(currentGroupFilter?.label.toLowerCase() ?? "")),
      )
      setOptionsGroup(getGroupList(list, "groupName"))
    }
  }, [list])

  const rowRenderer = ({ key, style, field }): ReactElement => {
    return (
      <div
        key={`${key}-${field.fieldName}`}
        style={{ ...style }}
        className="flex border-solid border-b border-gray-200 align-center w-full"
      >
        <div className="w-full grid grid-cols-3 gap-2 grid-flow-col" style={{ gridTemplateColumns: "3fr" }}>
          <div className="col-span-2">
            <div className="font-bold text-sm">{field.fieldName}</div>
            <OverflowText dark>{field.fieldLabel}</OverflowText>
          </div>
          {onUpdatePersonalInfo && (
            <div className="flex items-center justify-between">
              <FontAwesomeIcon
                icon={faFingerprint}
                className={`px-2 ${permissions ? "cursor-pointer" : ""}`}
                color={field.isPersonalInfo ? "var(--interactive-01)" : "#ccc"}
                onClick={() => {
                  onUpdatePersonalInfo(field)
                }}
              />
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-3">
        <Select
          className="w-1/2 select-wrapper"
          classNamePrefix="select"
          placeholder="Select group"
          options={optionsGroup || []}
          getOptionLabel={option => option.label}
          getOptionValue={option => option.value}
          isSearchable
          components={{ Option: CustomOption }}
          value={currentGroupFilter}
          onChange={item => {
            setCurrentGroupFilter(item as SingleValue<OptionGroup>)
            setFilteredList(
              list?.filter(field => field.groupName?.includes((item as SingleValue<OptionGroup>)?.label || "")) ?? [],
            )
          }}
          isClearable
          filterOption={filterOption}
        />

        {permissions && (
          <div>
            <Button
              kind="secondary"
              icon={iconAddOutline}
              iconDescription="Add new question"
              className="mr-2"
              onClick={() => {
                setOpenQuestionModal(prev => !prev)
              }}
            >
              Add new question
            </Button>

            <Button
              kind="secondary"
              icon={iconAddOutline}
              iconDescription="Add new group"
              onClick={() => {
                setOpenGroupModal(prev => !prev)
              }}
            >
              Add new group
            </Button>
          </div>
        )}
      </div>

      <WindowScroller scrollElement={window}>
        {({ height, scrollTop }) => (
          <AutoSizer disableHeight size={`size-${filteredList?.length}`}>
            {({ width }) => (
              <List
                ref={listReference}
                autoHeight
                width={width}
                height={height}
                rowCount={filteredList?.length ?? 0}
                rowHeight={45}
                scrollTop={scrollTop - 100}
                rowRenderer={({ key, index, style }: ListRowProps) =>
                  rowRenderer({ key, style, field: filteredList?.[index] })
                }
              />
            )}
          </AutoSizer>
        )}
      </WindowScroller>

      <Modal
        lazyLoad
        open={openQuestionModal}
        modalLabel="New question"
        primaryButtonText={loadingSaveQuestion ? "Saving..." : "Confirm"}
        secondaryButtonText="Cancel"
        onRequestSubmit={submitQuestion}
        onRequestClose={onClose}
        className="modal-lg"
        primaryButtonDisabled={loadingSaveQuestion}
      >
        <QuestionForm
          form={questionForm}
          setForm={setQuestionForm}
          xlsFormId={xlsFormId}
          groupForm={groupForm}
          setGroupForm={setGroupForm}
          choiceForm={choiceForm}
          setChoiceForm={setChoiceForm}
          setCategoryType={setCategoryType}
          categoryType={categoryType}
        />
      </Modal>

      <Modal
        lazyLoad
        open={openGroupModal}
        modalLabel="New group"
        primaryButtonText={loadingSaveGroup ? "Saving..." : "Confirm"}
        secondaryButtonText="Cancel"
        onRequestSubmit={submitGroup}
        onRequestClose={onClose}
        className="modal-lg"
        primaryButtonDisabled={loadingSaveGroup}
      >
        <GroupForm form={groupForm} setForm={setGroupForm} xlsFormId={xlsFormId} />
      </Modal>
    </div>
  )
}

export default FormList
